import { createRouter, createWebHistory, type RouteRecordRaw, type RouteLocationNormalized } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { usePerimeterStore } from '@/stores/perimeter'
import { useLoadingStore } from '@/stores/loading'
import { getUserFacingErrorMessage, isAuthenticationError } from '@/utils/api'

// Types pour les routes
declare module 'vue-router' {
  interface RouteMeta {
    requiresAuth?: boolean
    public?: boolean  // Route accessible sans authentification
    title?: string
  }
}

// Routes de l'application
const routes: RouteRecordRaw[] = [
  // Routes PowerBI - Reports fullscreen
  {
    path: '/arago-reports',
    component: () => import('@/layouts/general/RawIndex.vue'),
    children: [
      {
        name: 'Indicateurs Transparisés Full Page',
        path: 'transparisation/reporting',
        component: () => import('@/views/transpa/reporting/Reporting.vue'),
      },
      {
        name: 'Détails SCR Full Page',
        path: 'solvency/archives/details',
        component: () => import('@/views/solvency/archives/DetailsReport.vue'),
      },
      {
        name: 'Historique SCR Full Page',
        path: 'solvency/archives/historical',
        component: () => import('@/views/solvency/archives/HistoricalReport.vue'),
      },
      {
        name: 'Comparaison SCR Full Page',
        path: 'solvency/archives/comparison',
        component: () => import('@/views/solvency/archives/ComparisonReport.vue'),
      },
      // Routes ESG et Risk supprimées - Modules désactivés
      {
        name: 'Détails Transpa Full Page',
        path: 'transparisation/archives/details',
        component: () => import('@/views/transpa/archives/DetailsReport.vue'),
      },
      {
        name: 'Historique Transpa Full Page',
        path: 'transparisation/archives/historical',
        component: () => import('@/views/transpa/archives/HistoricalReport.vue'),
      },
      {
        name: 'Comparaison Transpa Full Page',
        path: 'transparisation/archives/comparison',
        component: () => import('@/views/transpa/archives/ComparisonReport.vue'),
      },
    ],
  },
  {
    path: '/',
    component: () => import('@/layouts/general/Index.vue'),
    children: [
      {
        name: 'Accueil',
        path: '',
        component: () => import('@/views/general/home/HomePage.vue'),
      },
      {
        name: 'Test Thème',
        path: '/theme-test',
        component: () => import('@/views/general/ThemeTestPage.vue'),
        meta: { public: true }, // Accessible sans authentification
      },
      {
        name: 'Intégration',
        path: '/integration',
        component: () => import('@/views/general/integration/IntegrationPage.vue'),
      },
      {
        name: 'Gestion des sauvegardes',
        path: '/archives',
        component: () => import('@/views/general/archives/ArchivesTablePage.vue'),
      },
      {
        name: 'Aide',
        path: '/help',
        component: () => import('@/views/general/help/HelpPage.vue'),
      },
      // Route Portefeuille (NEW - Remplace Transparisation + Calculs)
      {
        name: 'Portefeuille',
        path: '/portfolio',
        component: () => import('@/views/portfolio/PortfolioWorkspace.vue'),
      },
      // Route Périmètre - Wizard moderne
      {
        name: 'Configuration Périmètre',
        path: '/perimeter',
        component: () => import('@/views/general/perimeter/PerimeterFormWahoo.vue'),
      },
      {
        name: 'Transparisation - Indicateurs',
        path: 'transparisation/reporting',
        component: () => import('@/views/transpa/reporting/Reporting.vue'),
      },
      // Routes Archives Transparisation
      {
        name: 'Transparisation - Détails',
        path: 'transparisation/archives/details',
        component: () => import('@/views/transpa/archives/DetailsReport.vue'),
      },
      {
        name: 'Transparisation - Historique',
        path: 'transparisation/archives/historical',
        component: () => import('@/views/transpa/archives/HistoricalReport.vue'),
      },
      {
        name: 'Transparisation - Comparaison',
        path: 'transparisation/archives/comparison',
        component: () => import('@/views/transpa/archives/ComparisonReport.vue'),
      },
      // Routes Solvabilité II
      {
        name: 'Solvabilité II - Détails',
        path: 'solvency/archives/details',
        component: () => import('@/views/solvency/archives/DetailsReport.vue'),
      },
      {
        name: 'Solvabilité II - Historique',
        path: 'solvency/archives/historical',
        component: () => import('@/views/solvency/archives/HistoricalReport.vue'),
      },
      {
        name: 'Solvabilité II - Comparaison',
        path: 'solvency/archives/comparison',
        component: () => import('@/views/solvency/archives/ComparisonReport.vue'),
      },
      // Routes ESG et Risk supprimées - Modules désactivés
      // Routes Solvabilité II - Client Data
      {
        name: 'Participations Stratégiques',
        path: 'solvency/clientdata/strategicparticipations',
        component: () => import('@/views/solvency/clientdata/StrategicParticipationsPage.vue'),
      },
      {
        name: 'LTEI',
        path: 'solvency/clientdata/ltei',
        component: () => import('@/views/solvency/clientdata/LTEIPage.vue'),
      },
      {
        name: 'Actifs Intra Groupe',
        path: 'solvency/clientdata/intragroup',
        component: () => import('@/views/solvency/clientdata/IntraGroupPage.vue'),
      },
      {
        name: 'Exclusion SCR Concentration',
        path: 'solvency/clientdata/concentrationexclusion',
        component: () => import('@/views/solvency/clientdata/ConcentrationExclusionPage.vue'),
      },
      {
        name: 'Taux d\'endettement',
        path: 'solvency/clientdata/propertyleverage',
        component: () => import('@/views/solvency/clientdata/PropertyLeveragePage.vue'),
      },
      // Routes Solvabilité II - Parameters & Results
      {
        name: 'Solvabilité II - Paramètres SCR',
        path: 'solvency/parameters',
        component: () => import('@/views/solvency/parameters/ParametersPage.vue'),
      },
      {
        name: 'Solvabilité II - Résultats',
        path: 'solvency/results',
        component: () => import('@/views/solvency/results/ResultsPage.vue'),
      },
      // Routes ESG et Risk supprimées - Modules désactivés
      // Routes Simulation - TEMPORAIREMENT DÉSACTIVÉES (fichiers pas encore migrés)
      /*
      {
        name: 'Paramétrage Simulation',
        path: 'simulation/parameters',
        component: () => import('@/views/simulation/parameters/ParametersPage.vue'),
      },
      {
        name: 'Résultats Simulation',
        path: 'simulation/results',
        component: () => import('@/views/simulation/results/ResultsPage.vue'),
      },
      */
    ],
  },
  {
    path: '/no-page-access',
    name: 'NoPageAccess',
    component: () => import('@/layouts/errors/NoPageAccess.vue'),
  },
]

// Guard d'authentification (Vue Router 4 - pas de callback next)
async function authGuard(
  to: RouteLocationNormalized,
  _from: RouteLocationNormalized
): Promise<boolean | void> {
  // Autoriser les routes publiques (ex: /theme-test)
  if (to.meta.public === true) {
    return true
  }

  const authStore = useAuthStore()
  const perimeterStore = usePerimeterStore()
  const loadingStore = useLoadingStore()

  // Attendre l'initialisation MSAL avant toute vérification (requis par MSAL v4)
  await authStore.waitForMsalInitialization()

  if (!authStore.isAuthenticated) {
    // ⚠️ IMPORTANT: Ne pas interrompre les flux B2C actifs (password reset, etc.)
    if (authStore.isInB2CFlow()) {
      return false // Bloque la navigation mais ne force pas de redirect
    }
    
    if (authStore.resetLogin) {
      return false // Bloque la navigation si login en cours de reset
    }
    
    authStore.login()
    return false // Bloque la navigation pendant le login
  }

  try {
    // Activer le spinner pendant le chargement des données
    loadingStore.clearAppError()
    loadingStore.startLoading('Chargement de vos données...')

    // Définir le compte utilisateur dès qu'on est authentifié
    authStore.setUserAccount()

    // Initialiser les données utilisateur et vérifier tous les droits en parallèle
    // Optimisation : On lance toutes les requêtes en même temps
    const rightsChecks = await Promise.allSettled([
      authStore.checkTransparencyRights(),
      authStore.checkSolvencyRights(),
      authStore.checkSimulationRights(),
      // DÉSACTIVÉ - Modules ESG et Risk
      // authStore.checkEsgRights(),
      // authStore.checkRiskRights(),
      authStore.checkAdminRights(),
    ])

    const rightsError = rightsChecks.find(result => result.status === 'rejected')
    if (rightsError?.status === 'rejected') {
      throw rightsError.reason
    }

    const hasTransparencyRights = rightsChecks[0].status === 'fulfilled'
      ? rightsChecks[0].value
      : false

    if (!hasTransparencyRights) {
      await authStore.logout()
      return false // Bloque la navigation si pas de droits
    }

    // Toujours charger les rolenames pour avoir les vrais noms des clients
    await perimeterStore.loadRolenames()
    
    // Synchroniser le nom du rôle s'il existe déjà (restauré depuis sessionStorage)
    if (perimeterStore.client) {
      perimeterStore.syncClientName()
    }
    
    // Restaurer le périmètre depuis l'API si aucun périmètre n'est en session
    // Cela s'applique à TOUS les utilisateurs (admins inclus)
    // L'utilisateur peut ensuite réinitialiser via le bouton "Réinitialiser" du wizard
    if (!perimeterStore.client || perimeterStore.portfolios.length === 0) {
      // Pas de périmètre en session, tenter de restaurer depuis la BDD
      const loaded = await perimeterStore.loadPerimeterFromApi()
      
      // Si aucun périmètre sauvegardé, charger le client par défaut (utilisateurs normaux)
      if (!loaded && !authStore.isAdmin) {
        await perimeterStore.loadDefaultClient()
      }
    }

    // Configuration Google Tag Manager avec userID
    if (window.dataLayer) {
      window.dataLayer.push({
        userID: authStore.accountIdentifier,
      })
    }

    // Marquer l'application comme prête
    loadingStore.setAppReady()
    loadingStore.stopLoading()

    return true // Autorise la navigation
  } catch (error) {
    console.error('Erreur lors de l\'initialisation:', error)
    const errorMessage = getUserFacingErrorMessage(
      error,
      'Impossible d\'initialiser vos donnees. Rechargez la page puis reessayez.'
    )

    loadingStore.stopLoading()

    if (!loadingStore.appReady) {
      loadingStore.setAppError(errorMessage)
    }

    if (isAuthenticationError(error)) {
      await authStore.logout()
    }

    return false // Bloque la navigation en cas d'erreur
  }
}

// Création du router
const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
})

// Application du guard global
router.beforeEach(authGuard)

export default router
